import { useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const STATUS_FR = { interesse: "Intéressé", inscrit: "Inscrit", paye: "Payé", annule: "Annulé" };
const hasContact = (p) => (p.email || "").trim() || (p.phone || "").trim();

// Envoi du lien d'inscription : tout le groupe (intéressés / tous) ou sélection de participants, canal au choix.
export function SendRegLinkDialog({ event, parts, onClose, onSent, preselected = [] }) {
  const [mode, setMode] = useState(preselected.length ? "selection" : "interesse"); // interesse | tous | selection | indecis
  const [showTpl, setShowTpl] = useState(false);
  const [emailSubject, setEmailSubject] = useState(`🥋 Inscris-toi : ${event.event_title}`);
  const [emailText, setEmailText] = useState("Salut {prenom},\nvoici le lien pour t'inscrire à {titre}{date}. Les places sont limitées !");
  const [smsText, setSmsText] = useState("Inscris-toi a {titre} : {lien} — Institut Impact");
  const [selected, setSelected] = useState(new Set(preselected));
  const [channel, setChannel] = useState("both");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const eligible = useMemo(() => parts.filter(hasContact), [parts]);
  const targets = useMemo(() => {
    if (mode === "tous") return eligible;
    if (mode === "interesse") return eligible.filter((p) => p.status === "interesse");
    if (mode === "indecis") return eligible.filter((p) => p.status === "interesse" && p.link_clicked_at);
    return eligible.filter((p) => selected.has(p.id));
  }, [mode, eligible, selected]);
  const listed = useMemo(() => parts.filter((p) => !q || (p.full_name || "").toLowerCase().includes(q.toLowerCase())), [parts, q]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAllListed = () => setSelected(new Set(listed.filter(hasContact).map((p) => p.id)));

  const send = async () => {
    if (!targets.length) { toast.error("Aucun destinataire avec courriel ou téléphone."); return; }
    const ch = channel === "both" ? "courriel + SMS" : channel === "email" ? "courriel" : "SMS";
    if (!window.confirm(`Envoyer le lien d'inscription (${ch}) à ${targets.length} personne(s) ?\n\n${targets.slice(0, 8).map((p) => "• " + p.full_name).join("\n")}${targets.length > 8 ? `\n…+${targets.length - 8}` : ""}`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/dojo-events/${event.id}/send-registration-link`, {
        channel, participation_ids: targets.map((p) => p.id),
        email_subject: emailSubject, email_text: emailText.replace("{date}", event.start_date ? ` — ${event.start_date}` : ""), sms_text: smsText,
      });
      toast.success(`Lien envoyé — ${data.sent_email || 0} courriel(s), ${data.sent_sms || 0} SMS`);
      onSent?.(); onClose();
    } catch (e) { toast.error("Échec de l'envoi : " + (e?.response?.data?.detail || e.message)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="send-reg-link-dialog">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[88vh] flex flex-col rounded-2xl border border-zinc-700 bg-zinc-950 shadow-2xl">
        <div className="px-5 py-4 border-b border-zinc-800">
          <div className="text-[10px] tracking-[0.3em] uppercase text-sky-400">Lien d'inscription suivi</div>
          <h3 className="text-lg font-bold text-zinc-100">{event.event_title}</h3>
        </div>
        <div className="px-5 py-3 flex flex-wrap gap-2 border-b border-zinc-800 text-xs">
          {[["interesse", `Tout le groupe intéressé (${eligible.filter((p) => p.status === "interesse").length})`], ["tous", `Tous les participants (${eligible.length})`], ["indecis", `🔁 Relancer les indécis — ont cliqué sans s'inscrire (${eligible.filter((p) => p.status === "interesse" && p.link_clicked_at).length})`], ["selection", `Choisir les clients (${selected.size})`]].map(([k, l]) => (
            <button key={k} onClick={() => setMode(k)} data-testid={`reglink-mode-${k}`}
              className={`px-3 py-1.5 rounded-lg border ${mode === k ? "border-sky-400 text-sky-200 bg-sky-500/10" : "border-zinc-700 text-zinc-400 hover:text-zinc-200"}`}>{l}</button>
          ))}
          <div className="ml-auto flex gap-1">
            {[["both", "Courriel + SMS"], ["email", "Courriel"], ["sms", "SMS"]].map(([k, l]) => (
              <button key={k} onClick={() => setChannel(k)} data-testid={`reglink-channel-${k}`}
                className={`px-2.5 py-1.5 rounded-lg border ${channel === k ? "border-amber-400 text-amber-200" : "border-zinc-700 text-zinc-400"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="px-5 py-2 border-b border-zinc-800 text-xs">
          <button onClick={() => setShowTpl((v) => !v)} data-testid="reglink-template-toggle" className="text-amber-300 hover:text-amber-200">✏️ {showTpl ? "Masquer le message" : "Modifier le texte du courriel et du SMS"}</button>
          {showTpl && (
            <div className="mt-2 grid gap-2" data-testid="reglink-template-form">
              {(channel !== "sms") && <>
                <input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} data-testid="reglink-email-subject" className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-100" placeholder="Objet du courriel" />
                <textarea value={emailText} onChange={(e) => setEmailText(e.target.value)} rows={3} data-testid="reglink-email-text" className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-100" />
              </>}
              {(channel !== "email") && <textarea value={smsText} onChange={(e) => setSmsText(e.target.value)} rows={2} maxLength={300} data-testid="reglink-sms-text" className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-100" placeholder="SMS ({lien} ajouté automatiquement si absent)" />}
              <div className="text-[10px] text-zinc-500">Variables : {"{prenom}"} {"{nom}"} {"{titre}"} {"{date}"} {"{lien}"} · le bouton « S'inscrire » et le lien suivi sont toujours ajoutés au courriel.</div>
            </div>
          )}
        </div>
        <div className="px-5 py-3 flex items-center gap-2 text-xs border-b border-zinc-800">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un participant…" data-testid="reglink-search"
            className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-1.5 text-zinc-100" />
          {mode === "selection" && <><button onClick={selectAllListed} className="text-sky-300 hover:text-sky-200" data-testid="reglink-select-all">Tout cocher</button><button onClick={() => setSelected(new Set())} className="text-zinc-500 hover:text-zinc-300">Vider</button></>}
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-2 divide-y divide-zinc-900" data-testid="reglink-list">
          {listed.map((p) => {
            const inTargets = targets.some((t) => t.id === p.id);
            const noContact = !hasContact(p);
            return (
              <label key={p.id} className={`flex items-center gap-3 py-2 text-sm ${noContact ? "opacity-40" : ""} ${mode === "selection" ? "cursor-pointer" : ""}`} data-testid={`reglink-row-${p.id}`}>
                {mode === "selection" ? (
                  <input type="checkbox" disabled={noContact} checked={selected.has(p.id)} onChange={() => toggle(p.id)} className="accent-sky-500" data-testid={`reglink-check-${p.id}`} />
                ) : (
                  <span className={`w-2.5 h-2.5 rounded-full ${inTargets ? "bg-sky-400" : "bg-zinc-700"}`} />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-zinc-100 truncate">{p.full_name} <span className="text-zinc-500 text-xs">· {STATUS_FR[p.status] || p.status}{p.participant_type ? ` · ${p.participant_type}` : ""}</span></div>
                  <div className="text-[11px] text-zinc-500 truncate">{p.email || "—"} · {p.phone || "—"}{noContact ? " · aucun contact" : ""}{p.registration_link_sent_at ? ` · lien envoyé ${new Date(p.registration_link_sent_at).toLocaleDateString("fr-CA")}` : ""}{p.link_clicked_at ? " · 🔗 cliqué" : ""}</div>
                </div>
              </label>
            );
          })}
          {listed.length === 0 && <p className="text-sm text-zinc-500 py-6 text-center">Aucun participant.</p>}
        </div>
        <div className="px-5 py-4 border-t border-zinc-800 flex items-center justify-between gap-3">
          <span className="text-xs text-zinc-400" data-testid="reglink-count">{targets.length} destinataire(s) · chaque lien est suivi (on saura qui a cliqué)</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-2 rounded-lg border border-zinc-700 text-zinc-300 text-sm" data-testid="reglink-cancel">Annuler</button>
            <button onClick={send} disabled={busy || !targets.length} data-testid="reglink-send-btn"
              className="px-4 py-2 rounded-lg bg-sky-600 hover:bg-sky-500 disabled:opacity-40 text-white text-sm font-bold">{busy ? "Envoi…" : `📨 Envoyer à ${targets.length}`}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
